/**
 * Shortcuts (decision 66 bullets 1–2; table S in the part 10 log). Typed markup characters are stored as text (`\*`,
 * `\-`); a character that completes a recognised shortcut converts what it completes into markup. Each rule here only
 * finds the units to convert; `rewrite` (strict) makes the conversion and refuses it when the parse doesn't read it as
 * the construct (S10), so a rule can't make anything the grammar wouldn't.
 */
import { chapterModel, isAllowedLink, parseChapter } from '@cockatrice/core';
import type { ParsedChapter } from '@cockatrice/core';
import type { SyntaxNode } from '@lezer/common';
import type { ConvertOptions, Edit, Reading } from './rewrite.ts';
import { unitsOf } from './units.ts';
import type { Unit } from './units.ts';

/** Leaf blocks: their content is inline. */
const LEAVES: ReadonlySet<string> = new Set([
  'Paragraph',
  'ATXHeading1',
  'ATXHeading2',
  'ATXHeading3',
  'ATXHeading4',
  'ATXHeading5',
  'ATXHeading6',
  'SetextHeading1',
  'SetextHeading2',
  'CenteredBlock',
]);

/** S1: what a paragraph's first line may hold before a typed space to become a block (decision 66 bullet 1; S11). */
const BLOCK_SHORTCUT = /^(?:-|1\.|>|#{1,6})$/u;

/** S4: what a typed destination looks like to become a link: a bare URL, no blanks or angle brackets (L3: its scheme is the shared check's). */
const BARE_URL = /^(?:https?:\/\/|mailto:)[^\s<>]+$/iu;

/** S2: a letter or digit right before an opening run makes it mid-word. */
const WORD_CHARACTER = /[\p{L}\p{N}]/u;

/** A leaf holding a typed character, with its units: positions in its own lines' parse, plus `offset`. */
interface Leaf {
  readonly node: SyntaxNode;
  /** Where the leaf's local parse starts in the chapter. */
  readonly offset: number;
  readonly units: readonly Unit[];
  /** The leaf's lines as read, for `convert` to reuse where they are its window (G18). */
  readonly reading: Reading;
}

/**
 * The leaf holding the character at `at`, its own lines parsed alone with the last one's line end (they keep their
 * container prefixes, and inline content reads the same there), so a conversion costs the leaf, not a chapter-long
 * quote (*deep-fix r4*).
 */
function leafAt(parsed: ParsedChapter, at: number): Leaf | null {
  let leaf: SyntaxNode | null = parsed.tree.resolveInner(at, 1);
  while (leaf !== null && !LEAVES.has(leaf.name)) leaf = leaf.parent;
  if (leaf === null) return null;
  const { text } = parsed;
  const start = text.lastIndexOf('\n', leaf.from - 1) + 1;
  // A leaf ends at its last line's end (blanks there included), so with its line end, or at the chapter's end.
  const local = parseChapter(text.slice(start, leaf.to + 1));
  for (let node: SyntaxNode | null = local.tree.resolveInner(at - start, 1); node !== null; node = node.parent) {
    if (LEAVES.has(node.name)) {
      const own = node;
      const model = chapterModel(local);
      const units = unitsOf(local, model);
      return { node: own, offset: start, units: units.filter((u) => u.from >= own.from && u.to <= own.to), reading: { parsed: local, model, units } };
    }
  }
  return null;
}

/** An edit turning text units into the markup they show (a conversion). */
function convertUnits(units: readonly Unit[], offset: number): Edit[] {
  return units.filter((u) => u.kind === 'text').map((u) => ({ from: u.from + offset, to: u.to + offset, insert: [{ kind: 'convert', text: u.show }] }));
}

/** S1: a space typed at `at` after a block shortcut at the start of a paragraph's first line. */
function blockShortcut(leaf: Leaf, at: number): Edit[] | null {
  if (leaf.node.name !== 'Paragraph') return null;
  const local = at - leaf.offset;
  const before = leaf.units.filter((u) => u.to <= local);
  const marker = before.slice(0, -1);
  if (marker.some((u) => u.kind !== 'text') || !BLOCK_SHORTCUT.test(marker.map((u) => u.show).join(''))) return null;
  // The space typed is the paragraph's: every unit up to it is the marker, so the paragraph's first line starts with it.
  return convertUnits(before, leaf.offset);
}

/** The maximal run of units showing `char` that ends right before index `end` of `units`. */
function runBefore(units: readonly Unit[], end: number, char: string): { start: number; end: number } {
  let start = end;
  while (start > 0 && units[start - 1]?.show === char) start -= 1;
  return { start, end };
}

/** S2, S3: a delimiter typed at `at` closing a run opened earlier in the leaf. */
function delimiterShortcut(leaf: Leaf, at: number, char: string): Edit[] | null {
  const units = leaf.units;
  const typed = units.findIndex((u) => u.to === at - leaf.offset);
  if (typed < 0) return null;
  const closing = runBefore(units, typed + 1, char);
  const length = closing.end - closing.start;
  if (char === '^' && length !== 2) return null;
  // The nearest earlier run of the character, with something between.
  let open = closing.start - 1;
  while (open >= 0 && units[open]?.show !== char) open -= 1;
  if (open < 0 || open === closing.start - 1) return null;
  const opening = runBefore(units, open + 1, char);
  if (opening.end - opening.start < length) return null;
  // Decision 66: an opening `*` or `_` inside a word never starts emphasis (M*A*S*H).
  const previous = units[opening.start - 1];
  if (char !== '^' && previous?.kind === 'text' && WORD_CHARACTER.test(previous.show)) return null;
  return convertUnits([...units.slice(opening.end - length, opening.end), ...units.slice(closing.start, closing.end)], leaf.offset);
}

/** S4: `)` typed at `at` closing `[text](destination` with an allowed destination. */
function linkShortcut(leaf: Leaf, at: number): Edit[] | null {
  const units = leaf.units;
  const typed = units.findIndex((u) => u.to === at - leaf.offset);
  let paren = typed - 1;
  while (paren >= 0 && !(units[paren]?.show === '(' && units[paren - 1]?.show === ']')) paren -= 1;
  if (paren < 1) return null;
  const destination = units.slice(paren + 1, typed);
  if (destination.some((u) => u.kind !== 'text')) return null;
  const url = destination.map((u) => u.show).join('');
  // L3: the shape found here, the scheme decided by the one shared check (decision 44).
  if (!BARE_URL.test(url) || !isAllowedLink(url)) return null;
  let open = paren - 2;
  while (open >= 0 && units[open]?.show !== '[') open -= 1;
  const bang = units[open - 1];
  if (open < 0 || open === paren - 2 || (bang?.show === '!' && bang.kind === 'text')) return null;
  return convertUnits([units[open], ...units.slice(paren - 1, typed + 1)].filter((u) => u !== undefined), leaf.offset);
}

/** S5: `]` typed at `at` closing `[^label`. */
function noteShortcut(leaf: Leaf, at: number): Edit[] | null {
  const units = leaf.units;
  const typed = units.findIndex((u) => u.to === at - leaf.offset);
  let open = typed - 1;
  while (open >= 0 && !(units[open]?.show === '[' && units[open + 1]?.show === '^')) open -= 1;
  if (open < 0 || open + 2 >= typed) return null;
  return convertUnits(units.slice(open, typed + 1), leaf.offset);
}

/** The edits a conversion makes, and how `convert` makes them (G11–G18). */
export interface Conversion {
  readonly edits: Edit[];
  readonly options: ConvertOptions;
}

/** The edits the character `char` typed right before `at` completes in `leaf`, or null. */
function shortcutEdits(leaf: Leaf, at: number, char: string): Edit[] | null {
  switch (char) {
    case ' ':
      return blockShortcut(leaf, at);
    case '*':
    case '_':
    case '^':
      return delimiterShortcut(leaf, at, char);
    case ')':
      return linkShortcut(leaf, at);
    case ']':
      return noteShortcut(leaf, at);
    default:
      return null;
  }
}

/**
 * The conversion the character `char` typed right before `at` completes (table S), as edits for `convert` and the
 * options to make them with, or null. `parsed` holds the character already, as text.
 */
export function conversion(parsed: ParsedChapter, at: number, char: string): Conversion | null {
  const leaf = leafAt(parsed, at - 1);
  if (leaf === null) return null;
  const edits = shortcutEdits(leaf, at, char);
  // G16: a block shortcut changes its block's kind, so only an inline one is tried in its block alone.
  return edits === null ? null : { edits, options: { inline: char !== ' ', reading: leaf.reading } };
}
